/**
 * "Job type prices" — the first section of a customer's prices tab (gap 1, DEC-383, D-06).
 * Every job type this customer may use, with the price in force today and where it comes
 * from ("this customer" / "customer rate" / "standard"), or "No price". $0.00 is a price
 * (D-09). Prices are never deleted and stay until a later change (D-07): a change is a new dated row.
 *
 * Every row has a History; price managers (customer.canEdit, UX only: the API enforces
 * MANAGE_CUSTOMER_PRICES) also get "Set price" / "Change", which open JobTypePriceDialog.
 * The section fetches its own rows and shows its own error, so the page header and other
 * sections still render.
 */

import React from 'react';
import { Button } from 'src/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import type { PriceSectionProps } from '../../routes/CustomerPricesPage';
import { formatDay, priceCellText, priceText, type JobTypePriceRow } from './customer-prices';
import { useCustomerJobTypePrices } from './customer-prices-hooks';
import { JobTypePriceDialog } from './JobTypePriceDialog';
import { JobTypePriceHistory } from './JobTypePriceHistory';

const helpStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-text-secondary)' };

const UNIT_LABELS: Readonly<Record<string, string>> = {
  PER_BBL: 'per bbl',
  HOURLY: 'per hour',
  PER_LOAD: 'per load',
};

function unitLabel(billingMethod: string): string {
  return UNIT_LABELS[billingMethod] ?? billingMethod;
}

/** D-04: a price kept in a unit the job type no longer bills in. */
function otherUnitNote(row: JobTypePriceRow): string | null {
  if (row.otherUnit === null) return null;
  return `Set as ${priceText(row.otherUnit.rateCents, row.otherUnit.billingMethod)}; this job type now bills ${unitLabel(row.billingMethod)}. Set a new price.`;
}

export function JobTypePricesSection(props: PriceSectionProps): React.JSX.Element {
  const prices = useCustomerJobTypePrices(props.customer.id);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [historyOf, setHistoryOf] = React.useState<JobTypePriceRow | null>(null);

  if (prices.isLoading) {
    return (
      <p role="status" style={helpStyle}>
        Loading…
      </p>
    );
  }
  if (prices.isError || prices.data === undefined) {
    return (
      <div role="alert">
        <p style={{ color: 'var(--color-danger)', marginBottom: '8px' }}>
          Could not load job type prices. Try again.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={(): void => {
            void prices.refetch();
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  const rows = prices.data.rows;
  if (rows.length === 0) {
    return <p style={helpStyle}>This customer has no job types with a hauling price.</p>;
  }
  // The row as last loaded, so a reopened dialog sees the change it just saved.
  const editRow = editing === null ? undefined : rows.find((r) => r.familyId === editing);

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job type</TableHead>
            <TableHead>Unit</TableHead>
            <TableHead>Price today</TableHead>
            <TableHead>From</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => {
            const note = otherUnitNote(r);
            return (
              <TableRow key={r.familyId}>
                <TableCell>
                  <div style={{ fontWeight: 500 }}>{r.label}</div>
                  {note !== null && (
                    <div style={{ ...helpStyle, color: 'var(--color-warning)' }}>{note}</div>
                  )}
                </TableCell>
                <TableCell>{unitLabel(r.billingMethod)}</TableCell>
                <TableCell>{priceCellText(r)}</TableCell>
                <TableCell>
                  {r.special === null ? '—' : formatDay(r.special.effectiveFrom)}
                </TableCell>
                <TableCell>
                  {r.scheduled.length === 0 ? (
                    '—'
                  ) : (
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {r.scheduled.map((s) => (
                        <li key={`${s.effectiveFrom}:${String(s.revision)}`}>
                          {`${formatDay(s.effectiveFrom)}: ${priceText(s.rateCents, '')}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </TableCell>
                <TableCell style={{ whiteSpace: 'nowrap' }}>
                  {props.customer.canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      aria-label={`${r.special === null ? 'Set price' : 'Change'}: ${r.label}`}
                      onClick={(): void => setEditing(r.familyId)}
                    >
                      {r.special === null ? 'Set price' : 'Change'}
                    </Button>
                  )}{' '}
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`History: ${r.label}`}
                    onClick={(): void => setHistoryOf(r)}
                  >
                    History
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {editRow !== undefined && props.customer.canEdit && (
        <JobTypePriceDialog
          customer={props.customer}
          row={editRow}
          open={true}
          onOpenChange={(open): void => {
            if (!open) setEditing(null);
          }}
        />
      )}
      {historyOf !== null && (
        <JobTypePriceHistory
          customerId={props.customer.id}
          familyId={historyOf.familyId}
          label={historyOf.label}
          open={true}
          onOpenChange={(open): void => {
            if (!open) setHistoryOf(null);
          }}
        />
      )}
    </>
  );
}
